<template lang="pug">
  .team
    WtTile(:col="12" :is-loading="isLoading")
      BNotification(
        :has-icon="true"
        type="is-white"
        :active="teamMembersLoaded && userIsMemberOfThisTeam && !teamHasLeader && !hideNoLeaderMessage"
        :closable="true"
        @close="hideNoLeaderNotification"
      ) This team has no team leader! Team leaders help manage a team by adding team information and inviting new members. Help out your team by becoming a team leader!
        WtButton.is-block.mt-3(@click.native="makeMeTeamLeader" color="primary") YES, MAKE ME TEAM LEADER
    WtTile(ancestor vertical :is-loading="isLoading")
      WtTile(parent :col="12")
        WtTile(child :col="10" child-class="")
          WtTile.m-1
            WtTitle(h1) {{ team.name }}&nbsp;
            RouterLink(
              v-if="showTeamEditButton"
              :to="{ name: editTeamRoute, params: { teamId: team.id } }"
            )
              BIcon(icon="settings" size="is-small")
            WtJoinButton(v-if="teamLoaded && memberTeamsLoaded && memberTeamInvitesLoaded && coreProgramMeta.memberCanManageTeams" :team="team")
        WtTile.m-1(child :col="2" child-class="")
          WtButton(isRouterLink to="/teams" color="primary") BACK TO ALL TEAMS
      WtTile(:col="12")
        WtTile(vertical parent :col="4")
          WtTile(child :col="12" :is-loading="!teamLoaded")
            WtTitle(card-title v-if="!teamLoaded") Team Overview
            TeamOverview(v-if="teamLoaded" :team="team")
          WtTile(child :col="12" :is-loading="!teamLoaded")
            WtTitle(card-title v-if="!teamLoaded") Team Steps Overview
            TeamActivityOverview(v-if="teamLoaded" :team="team")
          WtTile(child :col="12" v-if="showFeaturedChallengeTile")
            FeaturedChallengeSmall(:featured-challenge="featuredChallenge")
          WtTile(child :col="12" :is-loading="!teamLoaded")
            WtTitle(card-title v-if="!teamLoaded") Team Members
            TeamMembers(v-if="teamLoaded" :team="team" :teamMembers="teamMembers" showTitle)

        WtTile(vertical parent :col="8")
          WtTile(child :col="12" :is-loading="!teamLoaded")
            BTabs(v-model="activeTab" :multiline="true")
              template(v-for="tab in ['Team Leaderboard', 'Team Average Over Time', 'Team Members Steps Over Time']")
                BTabItem(:key="tab" :label="tab")
            // TODO refactor to use keep-alive and make sure to fetch data here and pass down via props
            TeamLeaderboard(v-if="teamLoaded && activeTab === 0" :team="team")
            TeamStepsChart(v-if="teamLoaded && activeTab === 1" :team="team")
            TeamMembersStepsChart(v-if="teamLoaded && activeTab === 2" :team="team")


          WtTile(child :col="12" :is-loading="!teamLoaded" v-if="programLoaded && coreProgramMeta.teamDiscussion")
            WtTitle(card-title v-if="!teamLoaded") Team Discussion
            TeamDiscussion(
              v-if="teamLoaded"
              title="Team Discussion Board"
              :data-object="teamDiscussion"
              :team="team"
            )

</template>

<script>
import { mapActions, mapGetters } from "vuex";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtJoinButton from "@@/WtJoinButton/WtJoinButton";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import TeamLeaderboard from "@@/Teams/TeamLeaderboard";
import TeamMembers from "@@/Teams/TeamMembers";
import TeamDiscussion from "@@/Teams/TeamDiscussion";
import TeamOverview from "@@/Teams/TeamOverview";
import TeamActivityOverview from "@@/Teams/TeamActivityOverview";
import TeamStepsChart from "@@/Teams/TeamStepsChart";
import TeamMembersStepsChart from "@@/Teams/TeamMembersStepsChart";
const FeaturedChallengeSmall = () =>
  import(
    /* webpackChunkName: "FeaturedChallengeSmall" */ "@@/Challenges/FeaturedChallengeSmall"
  ).catch(() => {
    window.location.reload();
  });

export default {
  name: "Team",

  components: {
    WtButton,
    WtJoinButton,
    WtTile,
    WtTitle,
    TeamLeaderboard,
    TeamMembers,
    TeamDiscussion,
    TeamOverview,
    TeamActivityOverview,
    TeamStepsChart,
    TeamMembersStepsChart,
    FeaturedChallengeSmall
  },

  mixins: [analyticsTrackingMixin],

  data() {
    return {
      team: {
        id: null,
        name: null,
        description: null,
        public: null,
        inviteOnly: null,
        logo: null,
        maxMembers: null,
        numMembers: null
      },
      teamMembers: [],
      teamMembersLoaded: false,
      teamLoaded: false,
      activeTab: 0,
      isLoading: false,
      hideNoLeaderMessage: false
    };
  },

  computed: {
    ...mapGetters("program", [
      "programLoaded",
      "coreProgramMeta",
      "programTeams",
      "featuredChallenge",
      "currentFeaturedChallengeLoaded"
    ]),
    ...mapGetters("teams", [
      "teamDiscussion",
      "teamDiscussionLoaded",
      "memberJoiningTeam"
    ]),
    ...mapGetters("members", [
      "memberTeams",
      "memberTeamsLoaded",
      "memberTeamInvitesLoaded",
      "teamsMemberIsAdmin",
      "teamsMemberIsAdminLoaded"
    ]),

    showTeamEditButton() {
      return (
        this.team.id &&
        ((this.teamsMemberIsAdminLoaded &&
          !!this._.find(this.teamsMemberIsAdmin, { id: this.team.id })) ||
          this.$auth.user().isAdmin)
      );
    },

    editTeamRoute() {
      return this.$auth.user().isAdmin
        ? "TeamCenterSettingsTeamEdit"
        : "MemberTeamEdit";
    },

    showFeaturedChallengeTile() {
      return (
        this._.find(this.memberTeams, { id: this.team.id }) &&
        this._.get(this.featuredChallenge, "between", false) === "groups"
      );
    },

    teamHasLeader() {
      for (const member of this.teamMembers) {
        if (member.isTeamLeader) return true;
      }
      return false;
    },

    userIsMemberOfThisTeam() {
      const userId = this.$auth.user().id;
      for (const member of this.teamMembers) {
        if (member.id === userId) return true;
      }
      return false;
    }
  },

  watch: {
    // TODO/NOTE this is a solution in place of fetchAllMixin
    async memberJoiningTeam(newValue, oldValue) {
      if (!newValue && oldValue) {
        this.teamLoaded = false;
        this.team = await this.teamShow({ teamId: this.$route.params.teamId });
        this.teamLoaded = true;
      }
    }
  },

  mounted() {
    this.trackFeatureView("teams", "team_details");
    if (localStorage["hideNoLeaderMessage"]) this.hideNoLeaderMessage = true;
  },

  methods: {
    ...mapActions("teams", [
      "teamShow",
      "addMembersToTeam",
      "fetchTeamMembers",
      "promoteTeamLeader",
      "fetchTeamDiscussion"
    ]),
    ...mapActions("program", ["fetchFeaturedChallenge", "fetchProgramTeams"]),
    ...mapActions("members", [
      "fetchMemberTeams",
      "fetchMemberTeamInvites",
      "fetchTeamsMemberIsAdmin"
    ]),

    async makeMeTeamLeader() {
      this.isLoading = true;

      const payload = {
        teamId: this.team.id,
        memberId: this.$auth.user().id,
        showSnackbar: false
      };

      const response = await this.promoteTeamLeader(payload);
      if (response) {
        this.teamMembersLoaded = false;
        this.fetchTeamMembers({
          teamId: this.team.id,
          perPage: 15
        }).then(response => {
          this.teamMembers = response.data.data;
          // this.$set(this.team, "members", response.data.data);
          this.teamMembersLoaded = true;
        });

        await this.$store.dispatch(
          "snackBar",
          {
            duration: 5000,
            queue: false,
            message: "You are now the team leader!",
            type: "is-success"
          },
          { root: true }
        );
      }

      this.isLoading = false;
    },

    hideNoLeaderNotification() {
      localStorage["hideNoLeaderMessage"] = true;
      this.hideNoLeaderMessage = true;
    }
  },

  beforeRouteEnter(to, _, next) {
    next(async vm => {
      // check for invite code from email
      const invite = to.query.invite;
      if (invite) {
        let payload = {
          team: { id: to.params.teamId },
          memberIds: [vm.$auth.user().id],
          invite
        };
        vm.team = await vm.addMembersToTeam(payload);
        vm.teamLoaded = true;
      } else {
        if (!vm.memberJoiningTeam) {
          vm.team = await vm.teamShow({ teamId: vm.$route.params.teamId });
          vm.teamLoaded = true;
        }
      }
      vm.fetchTeamMembers({
        teamId: vm.team.id,
        perPage: vm.team.numMembers
      }).then(response => {
        // get up to 20 random users from team to display
        let randomMembersArray = [];
        if (response.data.data && response.data.data.length) {
          randomMembersArray = vm._.sampleSize(response.data.data, 20);
        }
        vm.teamMembers = randomMembersArray;
        vm.teamMembersLoaded = true;
      });

      // TODO/NOTE refactor this page to use fetchAllMixin?
      if (!vm.currentFeaturedChallengeLoaded) {
        vm.fetchFeaturedChallenge(vm.$auth.user().programId);
      }
      // accounts for accessing Team page directly
      if (!vm.teamsMemberIsAdminLoaded) {
        await vm.fetchTeamsMemberIsAdmin(vm.$auth.user().id);
      }
      if (!vm.memberTeamsLoaded) {
        await vm.fetchMemberTeams(vm.$auth.user().id);
      }
      if (!vm.memberTeamInvitesLoaded) {
        await vm.fetchMemberTeamInvites({ memberId: vm.$auth.user().id });
      }
      if (!vm.programTeamsLoaded) {
        await vm.fetchProgramTeams();
      }
      if (!vm.teamDiscussionLoaded) {
        await vm.fetchTeamDiscussion({ teamId: vm.team.id });
      }
    });
  }
};
</script>

<style lang="sass" scoped>
.team
  .team-header
    width: 100%
    .column
      padding-left: 0
    .team-header-title
      margin-bottom: .5rem
    .level-item
      justify-content: flex-start
  .close-leader-message
    position: absolute
    top: 10px
    right: 10px
</style>
